import { Node, Edge } from 'reactflow';
import { ArvoreIndicacao } from '../services/indicacaoService';

export interface IndicacaoNodeData {
  id: number;
  nome: string;
  status: string;
  createdAt: string;
  nivel: number;
  totalIndicados: number;
  isRoot?: boolean;
}

export interface IndicacaoEdgeData {
  indicadorId: number;
  indicadoId: number;
  nivel: number;
}

export type IndicacaoNode = Node<IndicacaoNodeData>;
export type IndicacaoEdge = Edge<IndicacaoEdgeData>;

/**
 * Converte dados da árvore de indicações para formato React Flow
 */
export const convertToReactFlow = (
  arvore: ArvoreIndicacao[],
  clienteRaizId: number,
  clienteRaizNome: string
): { nodes: IndicacaoNode[]; edges: IndicacaoEdge[] } => {
  const nodes: IndicacaoNode[] = [];
  const edges: IndicacaoEdge[] = [];

  // Configurações de layout
  const BASE_CHILD_SPACING = 120; // Espaçamento entre filhos do mesmo pai
  const MIN_PARENT_SPACING = 180; // Espaçamento mínimo entre pais
  const VERTICAL_SPACING = 150;

  // Função para calcular largura necessária para um pai acomodar seus filhos
  const calcularLarguraNecessaria = (quantidadeFilhos: number): number => {
    if (quantidadeFilhos <= 1) return 0;
    return (quantidadeFilhos - 1) * BASE_CHILD_SPACING;
  };

  // Função para calcular espaçamento necessário entre dois pais consecutivos
  const calcularEspacamentoPais = (pai1Filhos: number, pai2Filhos: number): number => {
    const larguraPai1 = calcularLarguraNecessaria(pai1Filhos);
    const larguraPai2 = calcularLarguraNecessaria(pai2Filhos);
    
    // Espaçamento = metade da largura do pai1 + espaçamento mínimo + metade da largura do pai2
    const espacoNecessario = (larguraPai1 / 2) + MIN_PARENT_SPACING + (larguraPai2 / 2);
    
    return Math.max(espacoNecessario, MIN_PARENT_SPACING);
  };

  // Adiciona nó raiz centralizado
  nodes.push({
    id: `root-${clienteRaizId}`,
    type: 'indicacaoRoot',
    position: { x: 0, y: 0 },
    data: {
      id: clienteRaizId,
      nome: clienteRaizNome,
      status: 'ativo',
      createdAt: new Date().toISOString(),
      nivel: 0,
      totalIndicados: arvore.length,
      isRoot: true
    }
  });

  // Função recursiva com algoritmo de reserva de espaço
  const processarNosRecursivo = (
    indicacoes: ArvoreIndicacao[],
    nivel: number,
    parentId: string,
    parentX: number = 0
  ): void => {
    if (indicacoes.length === 0 || nivel > 3) return;

    const yPosition = nivel * VERTICAL_SPACING;
    
    // ETAPA 1: Calcular posições dos pais baseado no espaço necessário para seus filhos
    const posicoesPais: number[] = [];
    let currentX = 0;
    
    if (indicacoes.length === 1) {
      // Caso especial: único filho centralizado no pai
      posicoesPais[0] = parentX;
    } else {
      // Calcular posições para múltiplos filhos
      const totalFilhos = indicacoes.length;
      
      // Calcular largura total necessária para todos os pais e seus filhos
      let larguraTotalNecessaria = 0;
      for (let i = 0; i < totalFilhos; i++) {
        const quantidadeFilhos = indicacoes[i].indicados.length;
        const larguraPai = calcularLarguraNecessaria(quantidadeFilhos);
        larguraTotalNecessaria += larguraPai;
        
        if (i < totalFilhos - 1) {
          // Adicionar espaçamento entre este pai e o próximo
          const proximoQuantidadeFilhos = indicacoes[i + 1].indicados.length;
          larguraTotalNecessaria += calcularEspacamentoPais(quantidadeFilhos, proximoQuantidadeFilhos);
        }
      }
      
      // Posição inicial para centralizar todos os pais em relação ao parent
      let startX = parentX - larguraTotalNecessaria / 2;
      
      // Calcular posição de cada pai
      for (let i = 0; i < totalFilhos; i++) {
        const quantidadeFilhos = indicacoes[i].indicados.length;
        const larguraPai = calcularLarguraNecessaria(quantidadeFilhos);
        
        // Posição do pai = início da sua área + metade da sua largura
        posicoesPais[i] = startX + larguraPai / 2;
        
        // Mover startX para o próximo pai
        if (i < totalFilhos - 1) {
          const proximoQuantidadeFilhos = indicacoes[i + 1].indicados.length;
          startX += larguraPai + calcularEspacamentoPais(quantidadeFilhos, proximoQuantidadeFilhos);
        }
      }
    }

    // ETAPA 2: Criar nós pais e processar seus filhos
    indicacoes.forEach((indicacao, index) => {
      const nodeId = `node-${indicacao.id}-level-${nivel}`;
      const xPosition = posicoesPais[index];

      // Cria nó pai
      nodes.push({
        id: nodeId,
        type: 'indicacaoNode',
        position: { x: xPosition, y: yPosition },
        data: {
          id: indicacao.id,
          nome: indicacao.nome,
          status: indicacao.status,
          createdAt: indicacao.createdAt,
          nivel: nivel,
          totalIndicados: indicacao.indicados.length
        }
      });

      // Cria edge do pai para o filho
      edges.push({
        id: `edge-${parentId}-${nodeId}`,
        source: parentId,
        target: nodeId,
        type: 'indicacaoEdge',
        data: {
          indicadorId: nivel === 1 ? clienteRaizId : parseInt(parentId.split('-')[1]),
          indicadoId: indicacao.id,
          nivel: nivel
        }
      } as IndicacaoEdge);

      // Processa recursivamente os filhos deste nó
      if (indicacao.indicados.length > 0) {
        processarNosRecursivo(indicacao.indicados, nivel + 1, nodeId, xPosition);
      }
    });
  };

  // Inicia o processamento a partir do nó raiz
  processarNosRecursivo(arvore, 1, `root-${clienteRaizId}`, 0);

  return { nodes, edges };
};

/**
 * Calcula estatísticas da rede
 */
export const calcularEstatisticasRede = (arvore: ArvoreIndicacao[]) => {
  const totalClientes = arvore.reduce((total, cliente) => {
    return total + 1 + calcularTotalRecursivo(cliente.indicados);
  }, 0);

  const profundidadeMaxima = arvore.length > 0 ? Math.max(...arvore.map(cliente => 
    1 + calcularProfundidadeRecursiva(cliente.indicados)
  )) : 0;

  const clientesPorNivel = Array.from({ length: 4 }, (_, i) => ({
    nivel: i + 1,
    quantidade: calcularClientesPorNivel(arvore, i + 1)
  }));

  return {
    totalClientes,
    profundidadeMaxima,
    clientesPorNivel,
    taxaCrescimento: calcularTaxaCrescimento(arvore)
  };
};

const calcularTotalRecursivo = (indicados: ArvoreIndicacao[]): number => {
  return indicados.reduce((total, cliente) => {
    return total + 1 + calcularTotalRecursivo(cliente.indicados);
  }, 0);
};

const calcularProfundidadeRecursiva = (indicados: ArvoreIndicacao[]): number => {
  if (indicados.length === 0) return 0;
  return Math.max(...indicados.map(cliente => 
    1 + calcularProfundidadeRecursiva(cliente.indicados)
  ));
};

const calcularClientesPorNivel = (arvore: ArvoreIndicacao[], nivel: number): number => {
  if (nivel === 1) return arvore.length;
  
  return arvore.reduce((total, cliente) => {
    return total + calcularClientesPorNivel(cliente.indicados, nivel - 1);
  }, 0);
};

const calcularTaxaCrescimento = (arvore: ArvoreIndicacao[]): number => {
  // Simula cálculo de taxa de crescimento baseado em datas
  const agora = new Date();
  const mesPassado = new Date(agora.getFullYear(), agora.getMonth() - 1, agora.getDate());
  
  let clientesRecentes = 0;
  let clientesAnteriores = 0;
  
  const contarPorPeriodo = (indicados: ArvoreIndicacao[]) => {
    indicados.forEach(cliente => {
      const dataCliente = new Date(cliente.createdAt);
      if (dataCliente >= mesPassado) {
        clientesRecentes++;
      } else {
        clientesAnteriores++;
      }
      contarPorPeriodo(cliente.indicados);
    });
  };
  
  contarPorPeriodo(arvore);
  
  return clientesAnteriores > 0 ? ((clientesRecentes / clientesAnteriores) * 100) : 0;
};

/**
 * Cores para diferentes níveis
 */
export const getCoresNivel = (nivel: number): { background: string; border: string; text: string } => {
  const cores = {
    0: { background: '#1976d2', border: '#0d47a1', text: '#ffffff' }, // Azul - Raiz
    1: { background: '#388e3c', border: '#1b5e20', text: '#ffffff' }, // Verde - Nível 1
    2: { background: '#f57c00', border: '#e65100', text: '#ffffff' }, // Laranja - Nível 2
    3: { background: '#7b1fa2', border: '#4a148c', text: '#ffffff' }, // Roxo - Nível 3
  };
  
  return cores[nivel as keyof typeof cores] || cores[3];
};

/**
 * Formata o nome do cliente para exibição no nó
 */
export const formatarNomeNode = (nome: string, maxLength: number = 20): string => {
  if (nome.length <= maxLength) return nome;
  return nome.substring(0, maxLength - 3) + '...';
}; 